<template>
  <teleport to="body">
    <div
      v-if="showEntityCard"
      ref="card"
      class="absolute z-[1000] w-72 rounded-xl border border-purple-300 bg-white p-4 shadow-lg"
      :style="positionStyle"
    >
      <div>
        <div class="mb-2 grid grid-cols-[95px_1fr] gap-2">
          <div class="text-sm text-gray-500">Name</div>
          <div class="truncate text-sm text-gray-900">{{ activeEntity?.name || 'Kein Name' }}</div>
        </div>
        <div class="mb-2 grid grid-cols-[95px_1fr] gap-2">
          <div class="text-sm text-gray-500">Beteiligung</div>
          <div class="truncate text-sm text-gray-900">
            {{ activeEntity?.role || 'Keine Beteiligung' }}
          </div>
        </div>
        <div class="mb-2 grid grid-cols-[95px_1fr] gap-2">
          <div class="text-sm text-gray-500">Geburtsdatum</div>
          <div class="truncate text-sm text-gray-900">
            {{ activeEntity?.birthDate || 'Kein Geburtsdatum' }}
          </div>
        </div>
        <div class="mb-2 grid grid-cols-[95px_1fr] gap-2">
          <div class="text-sm text-gray-500">E-Mail</div>
          <div class="truncate text-sm text-gray-900">
            {{ activeEntity?.email || 'Keine E-Mail' }}
          </div>
        </div>
        <div class="mb-2 grid grid-cols-[95px_1fr] gap-2">
          <div class="text-sm text-gray-500">Adresse</div>
          <div class="truncate text-sm text-gray-900">
            {{ activeEntity?.address || 'Keine Adresse' }}
          </div>
        </div>

        <button
          class="mt-4 flex w-[3/4] items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-1
            text-sm font-medium text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50"
          @click="handleShowAll"
        >
          Alle Daten zeigen
          <span class="ml-2 text-base font-normal text-gray-500">›</span>
        </button>
      </div>
    </div>
  </teleport>
</template>

<script setup>
  import { ref, watch, onMounted, onUnmounted, nextTick, inject, computed } from 'vue'
  import { useEntityCard } from '@/composables/useEntityCard'
  import { useRouter, useRoute } from 'vue-router'
  import { RelationshipTypeEnum } from '@/types/generated'
  const {
    activeEntity: entityFromRegistry,
    activeEntityElement,
    showEntityCard,
    hideCard
  } = useEntityCard()
  const caseData = inject('caseData')
  const router = useRouter()
  const route = useRoute()

  const emit = defineEmits(['show-all'])

  const card = ref(null)
  const positionStyle = ref({})

  import { entitiesDE } from '@/i18n/entity'
  // Role mapping from backend to frontend display
  const ROLE_MAPPING = entitiesDE

  // Compute the full entity data by combining the registry entity with case data
  const activeEntity = computed(() => {
    if (!entityFromRegistry.value || !caseData.value) return null

    // Find the matching entity in the case data
    const fullEntity = caseData.value.data.entities.find(
      (e) => e.entity.uuid === entityFromRegistry.value.uuid
    )
    if (!fullEntity) return entityFromRegistry.value

    const entity = fullEntity.entity

    // Format address if available
    let address = null
    if (entity.street && entity.street_number) {
      address = `${entity.street} ${entity.street_number}`
      if (entity.zip && entity.city) {
        address += `, ${entity.zip} ${entity.city}`
      }
    }

    // Get the role from the first relationship if it exists
    const firstRelationship = fullEntity.relationships?.[0]
    const role = firstRelationship
      ? ROLE_MAPPING[firstRelationship.relationship_type] || firstRelationship.relationship_type
      : null

    return {
      ...entityFromRegistry.value,
      role,
      birthDate: entity.date_of_birth,
      email: entity.email,
      uuid: entity.uuid,
      address
    }
  })

  const updatePosition = () => {
    if (!activeEntityElement.value || !card.value) return

    const triggerRect = activeEntityElement.value.getBoundingClientRect()
    const cardRect = card.value.getBoundingClientRect()

    let top = triggerRect.bottom + window.scrollY + 8
    let left = triggerRect.left + window.scrollX

    if (left + cardRect.width > window.innerWidth) {
      left = window.innerWidth - cardRect.width - 16
    }

    positionStyle.value = {
      top: `${top}px`,
      left: `${left}px`
    }
  }

  const handleShowAll = () => {
    hideCard()
    // Navigate to entity details page using current case UUID and entity UUID
    const caseUuid = caseData.value.uuid
    const entityUuid = activeEntity.value.uuid
    router.push(`/app/cases/${caseUuid}/entities/${entityUuid}/`)
  }

  const handleClickOutside = (event) => {
    if (!showEntityCard.value) return

    const isClickInsideCard = event.target.closest('.entity-card')
    const isClickInsideTrigger = event.target.closest('.entity')

    if (!isClickInsideCard && !isClickInsideTrigger) {
      hideCard()
    }
  }

  watch(
    () => showEntityCard.value,
    (newVal) => {
      if (newVal) {
        nextTick(() => {
          updatePosition()
        })
      }
    }
  )

  onMounted(() => {
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)
    document.addEventListener('click', handleClickOutside, true)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', updatePosition)
    window.removeEventListener('scroll', updatePosition)
    document.removeEventListener('click', handleClickOutside, true)
  })
</script>
